import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchServiceAreas, type ServiceArea } from '@/api/client';
import { serviceAreas as mockServiceAreas } from '@/mocks/serviceAreas';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function ServiceAreas() {
  const { t } = useTranslation('home');
  const [areaList, setAreaList] = useState<ServiceArea[]>(mockServiceAreas);

  useEffect(() => {
    let cancelled = false;
    fetchServiceAreas().then((data) => {
      if (cancelled) return;
      if (data.length === 0) {
        setAreaList(mockServiceAreas);
        return;
      }
      // fetchServiceAreas() already maps city_name→name, display_order→order, active filter applied
      setAreaList(data);
    }).catch(() => {
      if (!cancelled) setAreaList(mockServiceAreas);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="areas" className="w-full py-16 md:py-20 bg-brand-light">
      <div className="px-4 md:px-6 lg:px-24 max-w-7xl mx-auto">
        {/* Section Header */}
        <ScrollReveal className="mb-10 md:mb-14">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-brand-mint" />
            <span className="text-sm font-medium tracking-wider uppercase text-brand-mint">
              {t('areas_label')}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark font-heading leading-tight mb-4">
            {t('areas_title')}
          </h2>
          <div className="w-10 h-1 bg-brand-mint rounded-full" />
        </ScrollReveal>

        {/* Areas Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {areaList.map((area, idx) => (
            <ScrollReveal key={area.id} delay={idx * 60}>
              <div className="group flex items-center gap-3 p-4 md:p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-default">
                <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center shrink-0 group-hover:bg-brand-mint transition-colors duration-300">
                  <i className="ri-map-pin-line text-brand-dark group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-semibold text-brand-dark">
                    {area.name}
                  </h3>
                  <p className="text-xs text-brand-muted">{area.state}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Bottom note */}
        <ScrollReveal className="mt-8 text-center text-sm text-brand-muted">
          {t('areas_note')}
        </ScrollReveal>
      </div>
    </section>
  );
}